import { Stack, StackProps } from '@chakra-ui/react';
import { RequireOnly, SelectOption } from '@pelicargo/types';
import { Contact } from '@prisma/client';
import { useMemo } from 'react';
import { Configure, InstantSearch, useHits, useRefinementList, useSearchBox } from 'react-instantsearch-hooks';

import { useSelectedContact } from '../../hooks/contact/useSelectedContact';
import { useTypesense } from '../../hooks/useTypesense';
import { SimpleReactSelect } from '../SimpleReactSelect';

type ContactSelectProps = StackProps & {
  name: string;
  label: string;
};

const formatContactOptionLabel = (contact: RequireOnly<Contact, 'id' | 'preferred_name' | 'full_name' | 'email'>) => {
  const displayName = contact.preferred_name || contact.full_name || 'Unnamed';
  return `${displayName} <${contact.email ?? '-'}> (ID: ${contact.id})`;
};

const ContactSelect = ({ name, label, ...rest }: ContactSelectProps) => {
  const { hits } = useHits();

  useRefinementList({ attribute: 'associations.airports' });
  useRefinementList({ attribute: 'associations.accounts' });
  useRefinementList({ attribute: 'associations.providers' });
  useRefinementList({ attribute: 'associations.tags' });

  const { refine } = useSearchBox();

  // The Typesense hit list only holds the current page of search results, so
  // the stored id usually isn't in it — without this lookup the closed select
  // would render the bare id instead of a readable label.
  const { selectedId, contact: selectedContact } = useSelectedContact(name);

  const options = useMemo(() => {
    const hitOptions = hits.map((hit: any) => ({
      label: formatContactOptionLabel(hit),
      value: hit.id,
    })) as SelectOption[];

    const isSelectedInHits = hitOptions.some((option) => String(option.value) === String(selectedId));
    if (selectedContact && !isSelectedInHits) {
      hitOptions.unshift({ label: formatContactOptionLabel(selectedContact), value: String(selectedContact.id) });
    }

    return [...hitOptions, { label: 'None', value: null }];
  }, [hits, selectedContact, selectedId]);

  return (
    <Stack direction="column" spacing={0} {...rest}>
      <SimpleReactSelect name={name} label={label} onInputChange={refine} menuPlacement="auto" options={options} />
    </Stack>
  );
};

type Props = StackProps & {
  // Name in SimpleForm
  name: string;
  label: string;

  // Default Search State
  defaultQuery?: string;
  defaultAirlineIds?: string[];
  defaultAirportIds?: string[];
  defaultAccountIds?: string[];
  defaultTags?: string[];
};

export const ContactInput = ({
  defaultAccountIds,
  defaultAirlineIds,
  defaultAirportIds,
  defaultTags,
  name,
  label,
  ...rest
}: Props) => {
  const refinementList = useMemo(() => {
    return {
      'associations.accounts.id': defaultAccountIds ?? [],
      'associations.providers': defaultAirlineIds ?? [],
      'associations.airports': defaultAirportIds ?? [],
      'associations.tags': defaultTags ?? [],
    };
  }, [defaultAccountIds, defaultAirlineIds, defaultAirportIds, defaultTags]);

  const { searchClient } = useTypesense({
    additionalSearchParameters: {
      query_by: 'full_name, preferred_name, email',
      facet_by: 'associations.providers, associations.airports, associations.accounts.id, associations.tags',
      sort_by: 'full_name:asc',
    },
  });

  if (!searchClient) return null;

  return (
    <InstantSearch searchClient={searchClient} indexName="contacts">
      <Configure facetsRefinements={refinementList} />
      <ContactSelect name={name} label={label} {...rest} />
    </InstantSearch>
  );
};
